import React, {useState, useEffect, useRef} from 'react';
import {Alert, Compass, ContextMenu, Drawer, Navigation, ThreeDViewer} from '../../components';
import {Button} from "antd";
import Modal from "../../components/Layout-Components/Editor-Layout/Modal/Modal";
import SiteLoadLoader from "../../components/Layout-Components/Editor-Layout/Loader/SiteLoadLoader";
import Toolbar from "../../components/Layout-Components/Editor-Layout/Toolbar/Toolbar";
import Actionbar from "../../components/Layout-Components/Editor-Layout/Actionbar/Actionbar";
import StreetView from "../../components/Layout-Components/Editor-Layout/StreetView/StreetView";
import {useViewerStore} from '../../store/store';
import Addressbar from "../../components/Layout-Components/Editor-Layout/Addressbar/Addressbar";
import Sidebar from "../../components/Layout-Components/Editor-Layout/Sidebar/Sidebar";
import Menubar from "../../components/Layout-Components/Editor-Layout/Menubar/Menubar";
import {LoadingContext} from "../../store/loadingMessageStore";
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import {WarningIcon} from "@phosphor-icons/react";
import {useCurrentUser} from "../../contexts/UserContext";

const EditorPage: React.FC = () => {

    const {user} = useCurrentUser();
    const viewerAlert = useViewerStore(state => state.viewerAlert);
    const {hasCapability} = useCurrentUser();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [remainingTime, setRemainingTime] = useState(600); // Initialize with total idle limit in seconds
    const navigate = useNavigate();
    const startTimeRef = useRef<Date | null>(null);
    const intervalRef = useRef<number | null>(null);
    const [permanentIdle, setPermanentIdle] = useState(false);

    const handleOnIdle = () => {
        setIsModalOpen(true);
        startTimeRef.current = new Date();
        setRemainingTime(300); // 5 minutes countdown in seconds
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setRemainingTime(600); // Reset total idle limit
        clearTimer();
        reset();
    };

    const { reset } = useIdleTimer({
        timeout: 600000, // 10 minutes in milliseconds
        onIdle: handleOnIdle,
        debounce: 500,
        crossTab: true,
        events: ['mousemove', 'keydown', 'mousedown', 'touchstart'], // Add events to reset the timer
    });

    const clearTimer = () => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null; // reset for safety
        }
    };

    const idleMessage = hasCapability('Sites_Table') ? 
    `You will be rerouted to the Sites table`
    : `You will need to refresh the page`;

    useEffect(() => {
        if (isModalOpen) {
            intervalRef.current = window.setInterval(() => {
                if (!startTimeRef.current) return;
                const now = new Date();
                const elapsed = Math.floor((now.getTime() - startTimeRef.current.getTime()) / 1000); // Calculate elapsed time in seconds

                setRemainingTime(() => {
                    const newRemainingTime = 300 - elapsed; // Calculate remaining time
                    if (newRemainingTime <= 0) {
                        if(hasCapability('Sites_Table')){
                            navigate('/sites'); // Reroute user back to sites table
                            clearTimer();
                        }
                        else {
                            setPermanentIdle(true);
                        }
                    }
                    return newRemainingTime;
                });
            }, 1000);
        }

        return () => { clearTimer() };
    }, [isModalOpen, navigate]);

    useEffect(() => {
        const isModalOpen = viewerAlert.show && viewerAlert.isModal;
        useViewerStore.setState({isModalOpenInInterface: isModalOpen});
    }, [viewerAlert]);

    useEffect(() => {
        const loadingContext = new LoadingContext();
        loadingContext.setMessage({message: 'Initial setup...'});
        useViewerStore.setState({initialLoadLoadingContextRef: loadingContext});
    }, []);

    return (
        <div id={"editMainContainer"} className='editor'>
            <div className="Idle Alert">
                {(isModalOpen && !permanentIdle) &&
                    <Modal title="Idle Session" close={handleCloseModal}>
                        <p style={{paddingBottom: "10px", fontWeight: "bold"}}>This session has been idle.</p>
                        <p style={{paddingBottom: "10px"}}>
                            {idleMessage} in {Math.floor(remainingTime / 60)}:{(remainingTime % 60).toString().padStart(2, '0')} seconds due to inactivity.
                        </p>
                        <div style={{width: "100%", paddingBottom: "10px", display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                            <Button
                                type="text"
                                icon={<WarningIcon size={18}/>}
                                danger
                                className="no-click"
                            >
                                Note: Any unfinalized changes will be lost.
                            </Button>   
                        </div>      
                        <div className={"modal-bottom-action-button-container"}>
                            <Button
                                type="primary"
                                size="middle"
                                className="editor__control--finalize modal-confirm-button"
                                onClick={handleCloseModal}
                            >
                                I'm still here
                            </Button>
                        </div>
                    </Modal>
                }
                {(isModalOpen && permanentIdle) && //Do not reroute users to Sites table if they do not have access
                    <Modal title="Idle Session">
                        You have been idle for more than 15 minutes. Please refresh the page.
                    </Modal>
                }
            </div>

            <Menubar/>
            <Drawer/>
            <ContextMenu/>
            <Addressbar/>
            <Sidebar/>
            <SiteLoadLoader/>
            <StreetView/>
            <ThreeDViewer/>

            {
                viewerAlert.show && viewerAlert.isModal === true &&
                <Modal
                    title={viewerAlert.title ?? 'Modal'}
                    close={() => useViewerStore.setState({viewerAlert: {show: false, isModal: false}}) }
                >
                    {viewerAlert.message}
                </Modal>
            }
            {
                viewerAlert.show && viewerAlert.isModal === false &&
                <Alert
                    title={viewerAlert.title}
                    message={viewerAlert.message}
                    messageType={viewerAlert.messageType}
                    isCloseAfterDisplay={viewerAlert.isCloseAfterDisplay}
                    autoCloseSeconds={viewerAlert.autoCloseSeconds}
                    isModal={viewerAlert.isModal}
                    onClose={() => useViewerStore.setState({viewerAlert: {show: false}})}
                    show={viewerAlert.show}
                />
            }

            <Toolbar/>
            <Actionbar/>

            <div className='app__controls'>
                <Compass/>
                <Navigation/>
            </div>
        </div>
    );
}

export default EditorPage;